/**
 * Realtime Notification Bell Component
 * Uses Socket.IO for real-time notifications
 */

import React, { useState, useRef, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import { useSocket } from '../context/SocketContext';
import { useTheme } from '../context/ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import { NOTIFICATION_EVENTS } from '../constants/socketEvents';
// toast usage is handled via page-specific logic

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
}

const RealtimeNotificationBell: React.FC = () => {
  const { theme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const socket = useSocket();

  // Listen for notifications via Socket.IO
  useEffect(() => {
    if (socket) {
      const handleNotification = (data: any) => {
        console.log('Notification received in bell:', data);
        
        // Handle notification with event structure from backend
        let title = 'Notification';
        let message = '';
        let type = 'info';
        let notificationKey = '';
        
        if (data.event) {
          // Handle structured notification from backend
          switch (data.event) {
            case 'NEW_ORDER':
              title = '🎉 New Order';
              message = `Order #${data.data?.orderNumber || data.data?.orderId || 'new'} received`;
              type = 'order';
              // Create unique key for deduplication
              notificationKey = `NEW_ORDER_${data.data?.orderId || data.data?.orderNumber}`;
              break;
            case 'ORDER_UPDATE':
            case 'ORDER_STATUS_UPDATE':
              title = '📦 Order Update';
              message = `Order #${data.data?.orderNumber || data.data?.orderId} status: ${data.data?.status}`;
              type = 'order';
              notificationKey = `ORDER_UPDATE_${data.data?.orderId}_${data.data?.status}`;
              break;
            case 'kotCreated':
              title = '🍳 Kitchen Ticket';
              message = `KOT created for order #${data.data?.orderNumber}`;
              type = 'kot';
              notificationKey = `KOT_${data.data?.orderNumber}`;
              break;
            default:
              title = data.event;
              message = JSON.stringify(data.data);
              notificationKey = `${data.event}_${Date.now()}`;
          }
        } else {
          // Fallback for direct notification format
          title = data.title || 'Notification';
          message = data.message || JSON.stringify(data);
          type = data.type || 'info';
          notificationKey = `${type}_${Date.now()}`;
        }
        
        // Use a unique ID that includes the notification key to prevent exact duplicates
        const notificationId = notificationKey || Date.now().toString();
        
        // Check if this exact notification already exists (deduplication)
        setNotifications(prev => {
          const exists = prev.some(n => n.id === notificationId);
          if (exists) {
            console.log('Duplicate notification ignored:', notificationId);
            return prev;
          }
          
          const newNotification: Notification = {
            id: notificationId,
            type: type,
            title: title,
            message: message,
            timestamp: new Date(),
            read: false,
            data: data
          };
          
          // Increment unread count for new notification
          setUnreadCount(current => current + 1);
          
          // Add new notification and keep only last 10
          return [newNotification, ...prev].slice(0, 10);
        });
        
        // Don't show global toasts here - let each page handle its own toasts
        // The bell only stores notifications for the dropdown
        // This prevents duplicate toasts and allows page-specific behavior
      };

      // Listen ONLY for the notification event to avoid duplicates
      // First, remove any existing listener to prevent duplicates
      socket.off(NOTIFICATION_EVENTS.GENERAL, handleNotification as any);
      socket.on(NOTIFICATION_EVENTS.GENERAL, handleNotification);

      return () => {
        socket.off(NOTIFICATION_EVENTS.GENERAL, handleNotification);
      };
    }
  }, [socket]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg transition-colors duration-200"
        style={{
          backgroundColor: showDropdown ? (theme.glassmorphism?.card || 'rgba(255, 255, 255, 0.1)') : 'transparent',
          color: theme.text
        }}
      >
        {unreadCount > 0 ? (
          <BellAlertIcon className="h-6 w-6" style={{ color: theme.primary }} />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: theme.danger }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-lg shadow-xl z-50 overflow-hidden"
          style={{
            backgroundColor: theme.glassmorphism?.card || 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.glassmorphism?.border || 'rgba(255, 255, 255, 0.2)'}`
          }}
        >
          {/* Header */}
          <div 
            className="px-4 py-3 border-b flex items-center justify-between"
            style={{ borderColor: theme.glassmorphism?.border || 'rgba(255, 255, 255, 0.2)' }}
          >
            <div className="flex items-center gap-2">
              <h3 className="font-semibold" style={{ color: theme.text }}>
                Notifications
              </h3>
              {socket?.isConnected && (
                <span className="flex items-center gap-1 text-xs">
                  <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: theme.success || '#16A34A' }} />
                  <span style={{ color: theme.secondary }}>Live</span>
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs hover:underline"
                  style={{ color: theme.primary }}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs hover:underline"
                  style={{ color: theme.secondary }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <BellIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm" style={{ color: theme.secondary }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b hover:bg-opacity-50 transition-colors ${
                    !notification.read ? 'bg-opacity-10' : ''
                  }`}
                  style={{
                    borderColor: theme.glassmorphism?.border || 'rgba(255, 255, 255, 0.2)',
                    backgroundColor: !notification.read ? theme.button.secondary.bg : 'transparent'
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm" style={{ color: theme.text }}>
                        {notification.title}
                      </p>
                      <p className="text-sm mt-1" style={{ color: theme.secondary }}>
                        {notification.message}
                      </p>
                      <p className="text-xs mt-1" style={{ color: theme.secondary }}>
                        {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                      </p>
                    </div>
                    {!notification.read && (
                      <span className="h-2 w-2 rounded-full mt-2" style={{ backgroundColor: theme.primary || 'var(--action)' }} />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealtimeNotificationBell;